/**
 * CalendarSidebar Component
 * 
 * ARCHITECTURE (Jan 2026):
 * Provides a sidebar/drawer UI for selecting calendars in multi-calendar scenarios.
 * - Desktop: Always visible sidebar on the left
 * - Mobile: Collapsible drawer triggered by hamburger menu
 * 
 * Used by ManageCustomers page to scope customer lists to specific calendars.
 */

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Calendar, ChevronRight, Menu, X } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * CalendarSidebar Component
 * 
 * SINGLE CALENDAR OPTIMIZATION (Jan 2026):
 * If admin has only one calendar, sidebar is hidden and that calendar
 * is auto-selected. This provides a cleaner UI for single-calendar admins.
 */
const CalendarSidebar = ({ 
  selectedCalendarId, 
  onCalendarSelect,
  onCalendarsLoaded,  // NEW: Callback to inform parent of calendar count
  className = ''
}) => {
  // State for calendars list and mobile drawer visibility
  const [calendars, setCalendars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  
  const { getAuthHeaders } = useAuth();

  /**
   * Fetch calendars owned by the current admin.
   * Uses getAuthHeaders() to support Run As mode.
   * 
   * SINGLE CALENDAR OPTIMIZATION (Jan 2026):
   * If only one calendar exists, auto-select it and notify parent
   * via onCalendarsLoaded callback so sidebar can be hidden.
   */
  useEffect(() => {
    const fetchCalendars = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${BACKEND_URL}/api/calendars`, {
          headers: getAuthHeaders()
        });
        
        const calendarList = response.data.data || [];
        setCalendars(calendarList);
        
        // Auto-select first calendar if none selected
        if (!selectedCalendarId && calendarList.length > 0) {
          onCalendarSelect(calendarList[0].id);
        }
        
        // Notify parent of calendar count (for hiding sidebar if only one)
        if (onCalendarsLoaded) {
          onCalendarsLoaded(calendarList.length, calendarList);
        }
        
        setError('');
      } catch (err) {
        console.error('Error fetching calendars:', err);
        setError('Failed to load calendars');
      } finally {
        setLoading(false);
      }
    };

    fetchCalendars();
  }, [getAuthHeaders, selectedCalendarId, onCalendarSelect, onCalendarsLoaded]);

  /**
   * Handle calendar selection.
   * Closes mobile drawer after selection for better UX.
   */
  const handleCalendarClick = (calendarId) => {
    onCalendarSelect(calendarId);
    setIsMobileOpen(false); // Close mobile drawer after selection
  };

  /**
   * Get the currently selected calendar's name for mobile header display.
   */
  const selectedCalendarName = calendars.find(c => c.id === selectedCalendarId)?.name || 'Select Calendar';

  // Loading state - show skeleton only if we might have multiple calendars
  if (loading) {
    return (
      <div className={`bg-gray-50 dark:bg-gray-800 p-4 ${className}`}>
        <div className="animate-pulse space-y-3">
          <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-3/4"></div>
          <div className="h-10 bg-gray-200 dark:bg-gray-700 rounded"></div>
          <div className="h-10 bg-gray-200 dark:bg-gray-700 rounded"></div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className={`bg-gray-50 dark:bg-gray-800 p-4 ${className}`}>
        <p className="text-red-500 text-sm">{error}</p>
      </div>
    );
  }

  // No calendars state
  if (calendars.length === 0) {
    return (
      <div className={`bg-gray-50 dark:bg-gray-800 p-4 ${className}`}>
        <p className="text-gray-500 dark:text-gray-400 text-sm">
          No calendars found. Create a calendar first.
        </p>
      </div>
    );
  }

  // SINGLE CALENDAR OPTIMIZATION (Jan 2026):
  // Hide sidebar completely if admin has only one calendar.
  // The calendar is already auto-selected, no need for selection UI.
  if (calendars.length === 1) {
    return null;
  }

  return (
    <>
      {/* Mobile Header - Shows selected calendar with toggle button */}
      <div className="lg:hidden bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 p-3 flex items-center justify-between">
        <button
          onClick={() => setIsMobileOpen(true)}
          className="flex items-center gap-2 text-gray-700 dark:text-gray-200"
          data-testid="mobile-calendar-toggle"
        >
          <Menu className="w-5 h-5" />
          <Calendar className="w-4 h-4" />
          <span className="font-medium truncate max-w-[200px]">{selectedCalendarName}</span>
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>

      {/* Mobile Drawer Overlay */}
      {isMobileOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setIsMobileOpen(false)}
          data-testid="mobile-overlay"
        />
      )}

      {/* Sidebar / Mobile Drawer */}
      <aside
        className={`
          fixed lg:static inset-y-0 left-0 z-50
          w-64 bg-white dark:bg-gray-900 
          border-r border-gray-200 dark:border-gray-700
          transform transition-transform duration-300 ease-in-out
          ${isMobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
          ${className}
        `}
        data-testid="calendar-sidebar"
      >
        {/* Sidebar Header */}
        <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Calendar className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            <h2 className="font-semibold text-gray-800 dark:text-gray-100">Calendars</h2>
          </div>
          
          {/* Mobile close button */}
          <button
            onClick={() => setIsMobileOpen(false)}
            className="lg:hidden p-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded"
            data-testid="mobile-close-btn"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Calendar List */}
        <nav className="p-2 space-y-1 overflow-y-auto max-h-[calc(100vh-120px)]">
          {calendars.map((calendar) => (
            <button
              key={calendar.id}
              onClick={() => handleCalendarClick(calendar.id)}
              className={`
                w-full text-left px-3 py-2.5 rounded-lg
                flex items-center gap-3 transition-colors
                ${selectedCalendarId === calendar.id
                  ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border-l-4 border-blue-600'
                  : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
                }
              `}
              data-testid={`calendar-item-${calendar.id}`}
            >
              <div className={`
                w-2 h-2 rounded-full flex-shrink-0
                ${selectedCalendarId === calendar.id 
                  ? 'bg-blue-600 dark:bg-blue-400' 
                  : 'bg-gray-300 dark:bg-gray-600'
                }
              `} />
              <span className="truncate font-medium">{calendar.name}</span>
            </button>
          ))}
        </nav>
      </aside>
    </>
  );
};

export default CalendarSidebar;
